import React, { useEffect, useState } from 'react';
import { useRoutesStore } from '../../../../store/routesStore';
import { DeliveryRoute } from '../../../../services/routesApi';
import { apiClient } from '../../../../services/api';
import styles from './EditRouteModal.module.css';

interface DriverOption {
  id: number;
  full_name: string;
  email: string;
}

interface EditRouteModalProps {
  open: boolean;
  route: DeliveryRoute | null;
  onClose: () => void;
}

/**
 * Reassigns a delivery route to a different driver.
 * Backend exposes PATCH /delivery-routes/:id/assign — driver_id is the only mutable field.
 *
 * TODO (deferred): full "Create Route" flow needs a stop picker (pending order IDs)
 * and time-window builder. See backend src/services/routes.ts:416 createDeliveryRoute.
 */
export const EditRouteModal: React.FC<EditRouteModalProps> = ({ open, route, onClose }) => {
  const [driverId, setDriverId] = useState<string>('');
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const assignDriver = useRoutesStore((s) => s.assignDriver);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setDriverId(route?.assigned_driver_id ? String(route.assigned_driver_id) : '');
    setLoadingDrivers(true);
    apiClient
      .get<any>('/users', { limit: 100 })
      .then((res) => {
        const list: any[] = Array.isArray(res) ? res : res?.data ?? res?.users ?? [];
        const onlyDrivers = list.filter((u) => {
          const r = String(u?.role || '').toLowerCase();
          return r === 'driver';
        });
        setDrivers(onlyDrivers.map((u) => ({ id: u.id, full_name: u.full_name, email: u.email })));
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load drivers'))
      .finally(() => setLoadingDrivers(false));
  }, [open, route]);

  if (!open || !route) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!driverId) { setError('Pick a driver'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await assignDriver(route.id, Number(driverId));
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign driver');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Edit Route — {route.route_name}</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <p className={styles.note}>
            Reassign this delivery route to a different driver. To change the stop sequence,
            time windows, or scheduled date, the route must be recreated.
          </p>

          <label className={styles.field}>
            <span>Assigned Driver <span className={styles.required}>*</span></span>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              disabled={loadingDrivers}
              required
            >
              <option value="">{loadingDrivers ? 'Loading…' : '— Select a driver —'}</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.full_name} ({d.email})
                </option>
              ))}
            </select>
          </label>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting || loadingDrivers}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRouteModal;
